/* eslint-disable @typescript-eslint/no-shadow */
import React from 'react';
import { FileUploader } from '@citrite/citrix-files-ui';
import { Item as ShareFileItem } from '@citrite/sf-api';
import { useAppStore } from '../../../common/store';
import { DocumentTemplateTypes } from '../../../constants';
import { DocgenClient } from '../../../data/docgenClient';
import { handleErrorWithServerMessage } from '../../../data/errorHandler';
import { CopyFileFromSharefilePayload } from '../../../data/interfaces/DocumentTemplateFile';
import { t } from '../../../utils';
import { FileChooser as SfFileChooser } from '../FileChooser';
import { ProgressState } from '../SelectDocument/types';
import { CustomFileUploderStyle } from './CustomFileUploader.styled';

interface CustomFileUploaderProps {
	setUploadState: (state: ProgressState) => void;
	resetUploadState: () => void;
}

export function CustomFileUploader({
	setUploadState,
	resetUploadState,
}: CustomFileUploaderProps) {
	const { fetchThumbnailUrl, documentTemplate, updateDocumentTemplate } = useAppStore(
		state => ({
			fetchThumbnailUrl: state.fetchThumbnailUrl,
			updateDocumentTemplate: state.updateDocumentTemplate,
			documentTemplate: state.documentTemplate,
		})
	);

	const [isSfFilePickerVisible, setIsSfFilePickerVisible] =
		React.useState<boolean>(false);

	const onSfFilePickerClose = React.useCallback((file: ShareFileItem) => {
		setIsSfFilePickerVisible(false);
		if (file !== null) {
			uploadFromSharefile(file);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateFileName = (fileName: string) => {
		updateDocumentTemplate({ file: { ...documentTemplate.file, fileName } });
	};

	const completeUploadAndFetchThumbnail = (fileName: string) => {
		try {
			fetchThumbnailUrl(documentTemplate?.rid);

			updateFileName(fileName);
			setUploadState('ready');
		} catch {
			handleErrorWithServerMessage(t('errorMessages.fetchThumbnailUrlError'));
			resetUploadState();
		}
	};
	const uploadFromSharefile = async (file: ShareFileItem) => {
		setUploadState('started');

		const { documentTemplate } = useAppStore.getState();
		const payload: CopyFileFromSharefilePayload = {
			fileName: file.FileName,
			fileId: file.Id,
			overwrite: true,
		};
		try {
			const response = await DocgenClient.copyFileFromSharefile(
				documentTemplate?.rid,
				payload
			);
			if (response !== null) {
				setUploadState('storing');
				completeUploadAndFetchThumbnail(file.FileName);
			}
		} catch {
			handleErrorWithServerMessage(t('errorMessages.uploadFileError'));
			resetUploadState();
		}
	};

	const uploadFromComputer = async (uploadedLocalFiles: [File]) => {
		const file = uploadedLocalFiles[0];

		const { documentTemplate } = useAppStore.getState();
		setUploadState('started');
		const formData = new FormData();
		formData.append('content', file);

		try {
			const response = await DocgenClient.createFileUpload(
				formData,
				documentTemplate?.rid
			);
			if (response === '') {
				setUploadState('storing');
				completeUploadAndFetchThumbnail(file.name);
			}
		} catch {
			handleErrorWithServerMessage(t('errorMessages.uploadFileError'));
			resetUploadState();
		}
	};

	return (
		<CustomFileUploderStyle data-testid="CustomFileUploader">
			<FileUploader
				files={[]}
				allowFolders={false}
				forceSingleFile
				browseShareFile={() => {
					setIsSfFilePickerVisible(true);
				}}
				fileOrMimeTypes={DocumentTemplateTypes.Static.supportedFileExtensions}
				allowFilesToBeAdded
				onAddItems={uploadFromComputer}
			/>

			{isSfFilePickerVisible && <SfFileChooser onClose={onSfFilePickerClose} />}
		</CustomFileUploderStyle>
	);
}
